import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import authService, {
  AuthResponse,
  LoginRequest,
  Organization,
  RegisterRequest,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  User,
} from '../services/authService';

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  register: (data: RegisterRequest) => Promise<AuthResponse>;
  login: (data: LoginRequest) => Promise<AuthResponse>;
  logout: () => void;
  isAuthenticated: boolean;
  /** Refresh user/org from localStorage (e.g., after accept-invitation in another flow). */
  refreshAuthState: () => void;
  /** Replace the cached user, e.g., after the server returns a fresh payload. */
  setUser: (user: User | null) => void;
  /** Whether the current user holds the given permission key. */
  hasPermission: (key: string) => boolean;
  /** Whether the current user has any of the given permission keys. */
  hasAnyPermission: (...keys: string[]) => boolean;
  /** Whether the current user's role matches any of the supplied keys. */
  hasRole: (...roles: string[]) => boolean;
  updateCompanyName: (data: UpdateCompanyRequest) => Promise<UpdateCompanyResponse>;
  updatePassword: (data: UpdatePasswordRequest) => Promise<UpdatePasswordResponse>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(authService.getCurrentUser());
  const [organization, setOrganization] = useState<Organization | null>(authService.getCurrentOrganization());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUserState(authService.getCurrentUser());
    setOrganization(authService.getCurrentOrganization());
    setLoading(false);
  }, []);

  const refreshAuthState = useCallback(() => {
    setUserState(authService.getCurrentUser());
    setOrganization(authService.getCurrentOrganization());
  }, []);

  const setUser = useCallback((next: User | null) => {
    setUserState(next);
    if (next) {
      // keep localStorage in sync without exposing the private setter
      localStorage.setItem('user', JSON.stringify(next));
    }
  }, []);

  const register = async (data: RegisterRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.register(data);
      setUserState(response.data.user);
      setOrganization(response.data.organization || null);
      return response;
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (data: LoginRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(data);
      setUserState(response.data.user);
      setOrganization(response.data.organization || null);
      return response;
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUserState(null);
    setOrganization(null);
    setError(null);
  };

  const updateCompanyName = async (data: UpdateCompanyRequest) => {
    setLoading(true);
    try {
      const response = await authService.updateCompanyName(data);
      if (response.success && response.data?.user) {
        // Keep auth-related fields when backend returns only a profile subset.
        setUserState((prev) => ({ ...(prev || {}), ...response.data.user } as User));
      }
      return response;
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (data: UpdatePasswordRequest) => {
    setLoading(true);
    try {
      const response = await authService.updatePassword(data);
      return response;
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const permissionSet = useMemo(
    () => new Set<string>(Array.isArray(user?.permissions) ? (user!.permissions as string[]) : []),
    [user]
  );

  const hasPermission = useCallback(
    (key: string) => permissionSet.has(key),
    [permissionSet]
  );

  const hasAnyPermission = useCallback(
    (...keys: string[]) => keys.some((k) => permissionSet.has(k)),
    [permissionSet]
  );

  const hasRole = useCallback(
    (...roles: string[]) => (user?.role ? roles.includes(user.role) : false),
    [user]
  );

  const value: AuthContextType = {
    user,
    organization,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated: !!user,
    refreshAuthState,
    setUser,
    hasPermission,
    hasAnyPermission,
    hasRole,
    updateCompanyName,
    updatePassword,
    clearError,
  };

  return React.createElement(AuthContext.Provider, { value: value }, children);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
